import {
  ALL_MODULES,
  getTranslatedText,
  ENTITY_MAP,
  COMMON_CONSTANTS,
  ALL_SHARD_MODULES
} from '@nile/components';
import {
  AccountClient,
  getApiMethodAndKeyNames,
  apiResolver,
  GET_CUSTOM_FIELD_BY_NAME,
  GET_REQUESTERS_BY_CRITERIA,
  GET_TECHNICIANS_BY_CRITERIA,
  TicketClient,
  GET_FIELD
} from '@nile/graphql';
import { includes } from 'lodash';

const DEFAULT_CLIENT_CONDITION = {
  joinOperator: 'AND',
  operands: [{ attribute: 'accountId', operator: 'isNotEmpty', value: '' }]
};

/**
 * For entity type, if we want to call a specif api, this method will be called.
 * @param {object} item
 */
function getQuery(item) {
  const entity = item?.name?.split('.')[0] || '';
  switch (entity) {
    case 'accountManager':
      return GET_TECHNICIANS_BY_CRITERIA;
    default:
      return GET_REQUESTERS_BY_CRITERIA;
  }
}

/**
 * After getting values from the API, this value specifies
 * the method name to process the data
 * @param {object} item
 */
function getNameKey(item) {
  const entity = item?.split('.')[0] || '';
  switch (entity) {
    case 'accountManager':
      return 'getTechnicianList';
    default:
      return 'getRequesterList';
  }
}

function formatOperatorValues(allOperators, operator, intl) {
  return allOperators.getRuleBuilderOperators.operator_placeholders[
    operator
  ].map((el) => {
    return {
      ...el,
      label: getTranslatedText(intl, el.key),
      value: getTranslatedText(intl, el.key),
      name: el.key
    };
  });
}

/**
 * This method gives the query and client using which the API calls will be made
 * after selecting the attribute
 * @param {object} item
 * @param {String} operator
 */

function getPropValues(item, operator, allOperators, intl, currentModule) {
  if (item?.name) {
    if (item.isCustom) {
      if (includes(['date', 'datetime'], item.dataType) && operator) {
        return formatOperatorValues(allOperators, operator, intl);
      }
      const nameSplit = item.name.split('.');
      const colName = nameSplit[nameSplit.length - 1];
      return AccountClient.query({
        query: GET_FIELD,
        variables: {
          input: {
            columnName: colName,
            entity:
              currentModule.toLowerCase() ===
              ALL_SHARD_MODULES.client?.toLowerCase()
                ? ALL_SHARD_MODULES.client
                : ALL_SHARD_MODULES.requester
          }
        }
      });
    }
    const query = apiResolver.Client[item.name]?.api;
    const client = apiResolver.Client[item.name]?.client;
    const nameKey = getNameKey(item.name);
    let variables = {};
    switch (item.name) {
      default:
        variables = {
          listInfo: {}
        };
        break;
    }

    if (query && client) {
      return client.query({
        query: query,
        variables: COMMON_CONSTANTS.IS_INSIDE_INPUT_VARIABLES[nameKey]
          ? { input: variables }
          : variables
      });
    }
    if (operator) {
      return formatOperatorValues(allOperators, operator, intl);
    }
    return { client: AccountClient, query: getQuery(item) };
  }
  return {};
}

const getNameField = (name) => {
  switch (name) {
    case 'user.reportingManager.name':
    case 'reportingManager.name':
    case 'primaryContact.name':
      return 'getRequesterName';
    case 'user.reportingManager.email':
    case 'reportingManager.email':
    case 'primaryContact.email':
      return 'getRequesterEmail';
    case 'accountManager.name':
      return 'getTechName';
    case 'accountManager.email':
      return 'getTechEmail';
    case 'client.name':
      return 'name';
    default:
      return name;
  }
};

async function onAttributeSelect(params) {
  const {
    item: field,
    operator,
    fields,
    allOperators,
    intl,
    currentModule
  } = params;
  const allFields = fields;
  const selectedField = allFields.find((el) => el.name === field);
  const customQueryFields = [
    'primaryContact',
    'accountManager',
    'reportingManager',
    'client',
    'site'
  ];
  const nestedField = field.split('.')?.length > 1;

  const modifiedSelectedFields = {
    ...selectedField,
    name: getNameField(selectedField?.name)
  };

  if (nestedField && modifiedSelectedFields?.autoComplete) {
    return getPropValues(modifiedSelectedFields, null, allOperators);
  }

  if (modifiedSelectedFields?.dataType === 'boolean') {
    return [
      {
        name: 'true',
        label: 'true',
        value: true
      },
      {
        name: 'false',
        label: 'false',
        value: false
      }
    ];
  }
  let allValues = await getPropValues(
    modifiedSelectedFields,
    operator,
    allOperators,
    intl,
    currentModule
  );

  if (
    nestedField &&
    customQueryFields.some((v) => modifiedSelectedFields?.name.includes(v))
  ) {
    allValues = await getPropValues(modifiedSelectedFields, null, allOperators);
  }

  let res = [];

  if (modifiedSelectedFields?.name) {
    const apiMethodAndKeyNames = getApiMethodAndKeyNames(ENTITY_MAP.client);
    let method = apiMethodAndKeyNames[modifiedSelectedFields.name]?.method;
    const key = apiMethodAndKeyNames[modifiedSelectedFields.name]?.key;
    const nestedKey =
      apiMethodAndKeyNames[modifiedSelectedFields.name]?.nestedKey;
    const nestedTo =
      apiMethodAndKeyNames[modifiedSelectedFields.name]?.nestedTo;
    if (nestedTo) {
      allValues.data[method].forEach((el) => {
        res = res.concat(
          el[[nestedTo]].map((c) => {
            return {
              name: c[key],
              value: c[key],
              label: c[key]
            };
          })
        );
      });
    } else if (operator === 'on') {
      res = allValues;
    } else if (modifiedSelectedFields.isCustom) {
      method = 'getField';
      res = allValues.data
        ? allValues.data[method].options.map((el) => {
            return {
              name: el?.value,
              value: el?.value,
              label: el?.value
            };
          })
        : [];
    } else if (nestedKey) {
      res = allValues.data?.[method]?.[nestedKey]
        ? allValues.data?.[method]?.[nestedKey].map((el) => {
            return {
              name: el[key],
              value: el[key],
              label: el[key]
            };
          })
        : [];
    } else {
      res = allValues.data
        ? allValues.data[method].map((el) => {
            return {
              name: el[key],
              value: el[key],
              label: el[key]
            };
          })
        : [];
    }
  }
  return res;
}

/**
 * This method is called for autocomplete fields.
 * Usually apis will be called when we choose attributes. But for autocomplete,
 * on typing few characters, api will be called. This is the method.
 * @param {object} client
 * @param {gql} query
 * @param {object} params
 */
const onValueSearch = async (client, query, params) => {
  const attribute = params?.field?.split('.')[1];
  const nameKey = getNameKey(params.field);
  const res = await client.query({
    query: query,
    variables: {
      listInfo: {
        condition: {
          joinOperator: 'AND',
          operands: [
            {
              attribute: attribute || 'firstName',
              operator: 'startsWith',
              value: params.inputValue
            }
          ]
        }
      }
    }
  });
  const allOptions = res?.data?.[nameKey]
    ? res.data[nameKey].userList.map((el) => {
        return {
          name: `${el[attribute]}`,
          value: `${el[attribute]}`,
          label: `${el[attribute]}`
        };
      })
    : [];
  return allOptions;
};

export { onAttributeSelect, onValueSearch, DEFAULT_CLIENT_CONDITION };
